import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '@src/styles';
import { useRunsContext } from '../context/RunsContext';

const dropdownPaperSX = {
  marginTop: 0.5,
  backgroundColor: colors.brand.surface,
  backgroundImage: 'none',
  border: `1px solid ${colors.brand.line}`,
  borderRadius: '10px',
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
  color: colors.primary.white,
  '& .MuiAutocomplete-listbox': { padding: 0.5 },
  '& .MuiAutocomplete-option': {
    borderRadius: '6px',
    fontSize: '13px',
    '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
    '&[aria-selected="true"]': { backgroundColor: 'rgba(255, 160, 21, 0.12)' },
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { color: colors.brand.textSecondary, fontSize: '13px' },
} as const;

interface OrganizationPickerProps {
  size?: 'compact' | 'large';
  autoFocus?: boolean;
  onSelect?: (organizationId: string) => void;
}

export function OrganizationPicker({ size = 'compact', autoFocus = false, onSelect }: OrganizationPickerProps) {
  const { organizationId, organizationNames, setOrganizationId } = useRunsContext();
  const options = Object.entries(organizationNames)
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
  const selected = options.find((option) => option.id === organizationId) ?? null;
  const isLarge = size === 'large';

  return (
    <Autocomplete
      size="small"
      sx={{ width: isLarge ? '100%' : { xs: '100%', sm: 280 } }}
      options={options}
      value={selected}
      loading={options.length === 0}
      loadingText="Carregando organizações…"
      noOptionsText="Nenhuma organização encontrada"
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      filterOptions={(list, state) => {
        const term = state.inputValue.trim().toLowerCase();
        return term
          ? list.filter((option) => option.name.toLowerCase().includes(term) || option.id.toLowerCase().includes(term))
          : list;
      }}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps as typeof optionProps & { key: string };
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '13px', color: colors.primary.white }}>
                {option.name}
              </Typography>
              <Typography
                noWrap
                sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10.5px', color: colors.brand.textMuted }}
              >
                {option.id}
              </Typography>
            </Box>
          </Box>
        );
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, option) => {
        if (!option) return;
        if (onSelect) onSelect(option.id);
        else setOrganizationId(option.id);
      }}
      disableClearable={Boolean(selected)}
      slotProps={{ paper: { sx: dropdownPaperSX } }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder="Selecionar organização"
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon sx={{ fontSize: isLarge ? 19 : 17, color: colors.brand.textMuted, ml: 0.5 }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: isLarge ? 44 : 36,
              paddingY: '0 !important',
              fontSize: isLarge ? '14px' : '13px',
              color: colors.primary.white,
              backgroundColor: isLarge ? colors.background.primary : colors.brand.surface,
              borderRadius: '8px',
              '& fieldset': { borderColor: colors.brand.line },
              '&:hover fieldset': { borderColor: 'rgba(255, 160, 21, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: colors.primary.yellow, borderWidth: '1px' },
            },
            '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: colors.brand.textSecondary,
              '& svg': { color: 'inherit' },
            },
          }}
        />
      )}
    />
  );
}
